import { useEffect, useMemo, useState } from "react"
import {
  CARD_LIBRARY_STORAGE_KEY,
  CARD_LIBRARY_STORAGE_VERSION,
  CardLibraryStateSchema,
  EMPTY_CARD_LIBRARY,
} from "../cards/storage"
import {
  DECK_LIBRARY_STORAGE_KEY,
  DECK_LIBRARY_STORAGE_VERSION,
  DeckLibraryStateSchema,
  EMPTY_DECK_LIBRARY,
} from "../decks/storage"
import {
  EMPTY_HERO_LIBRARY,
  HERO_LIBRARY_STORAGE_KEY,
  HERO_LIBRARY_STORAGE_VERSION,
  HeroLibraryStateSchema,
} from "../heroes/storage"
import { useUserIdbState } from "../hooks/useIdbState"
import { useStoredState } from "../hooks/useStoredState"
import { useI18n } from "../i18n/useI18n"
import type { MatchState } from "../schemas"
import { usePublicSystemBattleBotsQuery } from "../system/query"
import {
  EMPTY_SYSTEM_BATTLE_BOTS,
  SYSTEM_BATTLE_BOTS_STORAGE_KEY,
  SYSTEM_BATTLE_BOTS_STORAGE_VERSION,
  SystemBattleBotsStateSchema,
} from "../system/storage"
import { createMatch } from "./engine/match/init"
import {
  getMatchSetupErrorLabel,
  getMatchSocketErrorLabel,
} from "./errorMessages"
import { MatchBoard } from "./MatchBoard"
import { buildSeatInput } from "./matchUtils"
import {
  clearPendingOnlineMatch,
  consumePendingMatch,
  getPendingOnlineMatch,
  type PendingOnlineMatchData,
  storePendingOnlineMatch,
} from "./pendingMatch"
import { useMatchWebSocket } from "./useMatchWebSocket"

export function MatchPage() {
  const { t } = useI18n()
  const [decks] = useUserIdbState(
    DECK_LIBRARY_STORAGE_KEY,
    DeckLibraryStateSchema,
    {
      defaultValue: EMPTY_DECK_LIBRARY,
      version: DECK_LIBRARY_STORAGE_VERSION,
    },
  )
  const [cards] = useUserIdbState(
    CARD_LIBRARY_STORAGE_KEY,
    CardLibraryStateSchema,
    { defaultValue: EMPTY_CARD_LIBRARY, version: CARD_LIBRARY_STORAGE_VERSION },
  )
  const [heroes] = useUserIdbState(
    HERO_LIBRARY_STORAGE_KEY,
    HeroLibraryStateSchema,
    {
      defaultValue: EMPTY_HERO_LIBRARY,
      version: HERO_LIBRARY_STORAGE_VERSION,
    },
  )
  const [systemBattleBots, setSystemBattleBots] = useStoredState(
    SYSTEM_BATTLE_BOTS_STORAGE_KEY,
    SystemBattleBotsStateSchema,
    {
      defaultValue: EMPTY_SYSTEM_BATTLE_BOTS,
      version: SYSTEM_BATTLE_BOTS_STORAGE_VERSION,
    },
  )
  const publicSystemBattleBotsQuery = usePublicSystemBattleBotsQuery()

  // Local (pass-and-play) match state
  const [matchState, setMatchState] = useState<MatchState | null>(null)
  const [isHotSeat, setIsHotSeat] = useState(false)

  // Online match connection info
  const [onlineMatch, setOnlineMatch] = useState<PendingOnlineMatchData | null>(
    null,
  )
  const [onlineRetryKey, setOnlineRetryKey] = useState(0)

  // Join-by-ID form state (shown in setup screen)
  const [joinMatchId, setJoinMatchId] = useState("")
  const [joinSeatId, setJoinSeatId] = useState<"seat_a" | "seat_b">("seat_a")

  // WebSocket — always called, passes null when not in online mode
  const {
    connectionState,
    disconnectDeadlines,
    state: wsState,
    status: wsStatus,
    wsError,
    sendCommand,
  } = useMatchWebSocket(
    onlineMatch?.matchId ?? null,
    onlineMatch?.seatId ?? null,
    onlineRetryKey,
  )

  // Local setup form state
  const [deckA, setDeckA] = useState("")
  const [deckB, setDeckB] = useState("")
  const [nameA, setNameA] = useState(() => t("match.page.player1"))
  const [nameB, setNameB] = useState(() => t("match.page.player2"))
  const [setupErrors, setSetupErrors] = useState<string[]>([])

  // Pick up any pending match on mount
  useEffect(() => {
    const pendingOnline = getPendingOnlineMatch()
    if (pendingOnline) {
      setOnlineMatch(pendingOnline)
      return
    }
    const pending = consumePendingMatch()
    if (pending) {
      setMatchState(pending.matchState)
      setIsHotSeat(pending.hotSeat === true)
    }
  }, [])

  useEffect(() => {
    if (onlineMatch) {
      storePendingOnlineMatch(onlineMatch)
      return
    }
    clearPendingOnlineMatch()
  }, [onlineMatch])

  useEffect(() => {
    if (publicSystemBattleBotsQuery.data) {
      setSystemBattleBots(publicSystemBattleBotsQuery.data)
    }
  }, [publicSystemBattleBotsQuery.data, setSystemBattleBots])

  const cardDefs = useMemo(
    () => new Map(cards.map((r) => [r.card.id, r.card])),
    [cards],
  )
  const heroDefs = useMemo(
    () => new Map(heroes.map((r) => [r.hero.heroId, r.hero])),
    [heroes],
  )
  const resolvedSystemBattleBots =
    publicSystemBattleBotsQuery.data ?? systemBattleBots

  function resetOnlineMatch() {
    clearPendingOnlineMatch()
    setOnlineRetryKey(0)
    setOnlineMatch(null)
  }

  function retryOnlineMatch() {
    setOnlineRetryKey((previous) => previous + 1)
  }

  // --- Online match board ---

  if (onlineMatch) {
    if (wsStatus === "connecting" || (wsStatus === "open" && !wsState)) {
      return (
        <section className="panel">
          <p className="meta">
            {t("match.page.connecting", { matchId: onlineMatch.matchId })}
          </p>
        </section>
      )
    }

    if ((wsStatus === "error" || wsStatus === "closed") && !wsState) {
      return (
        <section className="panel panel-danger">
          <h2>{t("match.page.connectionFailed")}</h2>
          <p>
            {wsError
              ? getMatchSocketErrorLabel(t, wsError)
              : t("match.page.connectionClosed")}
          </p>
          <div className="button-row">
            <button
              className="primary-button"
              onClick={retryOnlineMatch}
              type="button"
            >
              {t("error.route.retry")}
            </button>
            <button
              className="ghost-icon-button"
              onClick={resetOnlineMatch}
              type="button"
            >
              {t("match.page.backToSetup")}
            </button>
          </div>
        </section>
      )
    }

    if (wsState) {
      return (
        <MatchBoard
          cardDefs={cardDefs}
          controlledState={wsState}
          heroDefs={heroDefs}
          initialState={wsState}
          onReset={resetOnlineMatch}
          onSendCommand={sendCommand}
          disconnectDeadlines={disconnectDeadlines}
          onlineStatus={wsStatus}
          seatConnectionState={connectionState}
          viewerSeatId={onlineMatch.seatId}
        />
      )
    }
  }

  // --- Local (pass-and-play) match board ---

  if (matchState) {
    return (
      <MatchBoard
        cardDefs={cardDefs}
        heroDefs={heroDefs}
        hotSeat={isHotSeat}
        initialState={matchState}
        onReset={() => {
          setMatchState(null)
          setIsHotSeat(false)
          setSetupErrors([])
        }}
      />
    )
  }

  // --- Setup screen ---

  function handleStartMatch() {
    const errs: string[] = []
    if (!deckA) errs.push(t("match.setup.error.player1Deck"))
    if (!deckB) errs.push(t("match.setup.error.player2Deck"))
    if (errs.length > 0) {
      setSetupErrors(errs)
      return
    }

    const seatAResult = buildSeatInput(
      deckA,
      "seat_a",
      "player_1",
      nameA.trim() || t("match.page.player1"),
      decks,
      cards,
      heroes,
    )
    const seatBResult = buildSeatInput(
      deckB,
      "seat_b",
      "player_2",
      nameB.trim() || t("match.page.player2"),
      decks,
      cards,
      heroes,
    )

    if (seatAResult.error) {
      errs.push(getMatchSetupErrorLabel(t, seatAResult.error))
    }
    if (seatBResult.error) {
      errs.push(getMatchSetupErrorLabel(t, seatBResult.error))
    }
    if (errs.length > 0) {
      setSetupErrors(errs)
      return
    }

    const seed = `local_${Date.now()}`
    if (!seatAResult.input || !seatBResult.input) {
      setSetupErrors([t("match.setup.error.deckLoad")])
      return
    }
    const { state } = createMatch({
      matchId: `match_${Date.now().toString(36)}`,
      seatA: seatAResult.input,
      seatB: seatBResult.input,
      seed,
      systemBattleBots: resolvedSystemBattleBots,
    })

    setSetupErrors([])
    setMatchState(state)
  }

  function handleJoinMatch() {
    const id = joinMatchId.trim()
    if (!id) return
    setOnlineRetryKey(0)
    setOnlineMatch({ matchId: id, seatId: joinSeatId, challengeId: "" })
  }

  return (
    <section className="panel management-panel">
      <div className="page-header">
        <div>
          <p className="eyebrow">{t("match.page.pageEyebrow")}</p>
          <h2>{t("match.page.pageTitle")}</h2>
        </div>
      </div>

      {/* Local pass-and-play setup */}
      <h3 className="match-setup-heading">{t("match.page.localHeading")}</h3>
      <div className="match-setup-form">
        <div className="match-setup-seat">
          <h4>{t("match.page.player1Seat")}</h4>
          <div className="field">
            <label htmlFor="match-name-a">
              <span>{t("common.displayName")}</span>
            </label>
            <input
              className="text-input"
              id="match-name-a"
              onChange={(e) => setNameA(e.target.value)}
              type="text"
              value={nameA}
            />
          </div>
          <div className="field">
            <label htmlFor="match-deck-a">
              <span>{t("common.deck")}</span>
            </label>
            <select
              className="text-input"
              id="match-deck-a"
              onChange={(e) => setDeckA(e.target.value)}
              value={deckA}
            >
              <option value="">{t("friends.deckPicker.placeholder")}</option>
              {decks.map((r) => (
                <option key={r.deck.deckId} value={r.deck.deckId}>
                  {r.deck.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="match-setup-vs">{t("match.page.vs")}</div>

        <div className="match-setup-seat">
          <h4>{t("match.page.player2Seat")}</h4>
          <div className="field">
            <label htmlFor="match-name-b">
              <span>{t("common.displayName")}</span>
            </label>
            <input
              className="text-input"
              id="match-name-b"
              onChange={(e) => setNameB(e.target.value)}
              type="text"
              value={nameB}
            />
          </div>
          <div className="field">
            <label htmlFor="match-deck-b">
              <span>{t("common.deck")}</span>
            </label>
            <select
              className="text-input"
              id="match-deck-b"
              onChange={(e) => setDeckB(e.target.value)}
              value={deckB}
            >
              <option value="">{t("friends.deckPicker.placeholder")}</option>
              {decks.map((r) => (
                <option key={r.deck.deckId} value={r.deck.deckId}>
                  {r.deck.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {setupErrors.length > 0 && (
        <ul className="form-errors" style={{ marginTop: "1rem" }}>
          {setupErrors.map((e) => (
            <li className="field-error" key={e}>
              {e}
            </li>
          ))}
        </ul>
      )}

      <div className="match-setup-actions">
        <button
          className="primary-button"
          onClick={handleStartMatch}
          type="button"
        >
          {t("match.page.startLocal")}
        </button>
      </div>

      {/* Online join-by-ID */}
      <h3 className="match-setup-heading" style={{ marginTop: "2rem" }}>
        {t("match.page.onlineHeading")}
      </h3>
      <p className="meta">{t("match.page.joinByIdDescription")}</p>
      <div className="match-join-form">
        <div className="field">
          <label htmlFor="join-match-id">
            <span>{t("match.page.joinId")}</span>
          </label>
          <input
            className="text-input"
            id="join-match-id"
            onChange={(e) => setJoinMatchId(e.target.value)}
            placeholder={t("match.page.joinIdPlaceholder")}
            type="text"
            value={joinMatchId}
          />
        </div>
        <div className="field">
          <label htmlFor="join-seat-id">
            <span>{t("match.page.yourSeat")}</span>
          </label>
          <select
            className="text-input"
            id="join-seat-id"
            onChange={(e) =>
              setJoinSeatId(e.target.value as "seat_a" | "seat_b")
            }
            value={joinSeatId}
          >
            <option value="seat_a">{t("match.page.seatA")}</option>
            <option value="seat_b">{t("match.page.seatB")}</option>
          </select>
        </div>
      </div>
      <div className="match-setup-actions">
        <button
          className="primary-button"
          disabled={!joinMatchId.trim()}
          onClick={handleJoinMatch}
          type="button"
        >
          {t("friends.challenges.joinMatch")}
        </button>
      </div>
    </section>
  )
}
